import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'baseui/modal';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Select } from 'baseui/select';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import type { Estate, EstateGroup } from '../../api/generated';

interface EstateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Estate) => void;
  initialData?: Estate;
  isLoading?: boolean;
  title: string;
  estateGroups: EstateGroup[];
  selectedGroupId?: number;
}

export const EstateForm: React.FC<EstateFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  title,
  estateGroups,
  selectedGroupId,
}) => {
  const [formData, setFormData] = useState<Estate>({
    name: '',
    estate_group_id: selectedGroupId,
    estate_type_id: undefined,
    zone_id: undefined,
    estate_access_typeid: undefined,
    user_type_id: undefined,
    status: '',
    show_in_app: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        show_in_app: initialData.show_in_app ?? true,
      });
    } else {
      setFormData({
        name: '',
        estate_group_id: selectedGroupId,
        estate_type_id: undefined,
        zone_id: undefined,
        estate_access_typeid: undefined,
        user_type_id: undefined,
        status: '',
        show_in_app: true,
      });
    }
    setErrors({});
  }, [initialData, isOpen, selectedGroupId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.estate_group_id) {
      newErrors.estate_group_id = 'Estate Group is required';
    }

    if (!formData.status?.trim()) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof Estate, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const estateGroupOptions = estateGroups.map(group => ({
    label: group.name || `Group ${group.id}`,
    id: group.id,
  }));

  const selectedEstateGroup = estateGroupOptions.filter(option => 
    option.id === formData.estate_group_id
  );

  const statusOptions = [
    { id: "planning", label: "Planning" },
    { id: "preconstruction", label: "Preconstruction" },
    { id: "underconstruction", label: "Under Construction" },
    { id: "paused", label: "Paused" },
    { id: "completed", label: "Completed" },
    { id: "delivered", label: "Delivered" },
    { id: "cancelled", label: "Cancelled" }
  ];

  const selectedStatus = statusOptions.filter(option => 
    option.id === formData.status
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeable={!isLoading}
      role="dialog"
    >
      <ModalHeader>{title}</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <FormControl
            label="Name"
            error={errors.name}
          >
            <Input
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', (e.target as HTMLInputElement).value)}
              placeholder="Enter estate name"
              error={!!errors.name}
              disabled={isLoading}
              required
              overrides={{
                Root: {
                  style: {
                    backgroundColor: '#F6F6F6',
                    borderRadius: '4px',
                  }
                }
              }}
            />
          </FormControl>

          <FormControl
            label="Estate Group"
            error={errors.estate_group_id}
          >
            <Select
              options={estateGroupOptions}
              value={selectedEstateGroup}
              placeholder="Select an estate group"
              onChange={params => {
                const selectedOption = params.value[0];
                handleInputChange('estate_group_id', selectedOption?.id);
              }}
              disabled={isLoading || !!selectedGroupId} // Disable if group is pre-selected
              error={!!errors.estate_group_id}
              overrides={{
                Root: {
                  style: {
                    backgroundColor: '#F6F6F6',
                    borderRadius: '4px',
                  }
                }
              }}
            />
          </FormControl>

          <FormControl
            label="Status"
            error={errors.status}
          >
            <Select
              options={statusOptions}
              value={selectedStatus}
              placeholder="Select a status"
              onChange={params => {
                const selectedOption = params.value[0];
                handleInputChange('status', selectedOption?.id);
              }}
              disabled={isLoading}
              error={!!errors.status}
              overrides={{
                Root: {
                  style: {
                    backgroundColor: '#F6F6F6',
                    borderRadius: '4px',
                  }
                }
              }}
            />
          </FormControl>

          <div style={{ marginTop: '8px' }}>
            <Checkbox
              checked={formData.show_in_app ?? true}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                show_in_app: (e.target as HTMLInputElement).checked,
              }))}
              disabled={isLoading}
              labelPlacement={LABEL_PLACEMENT.right}
            >
              Show in App
            </Checkbox>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            kind="tertiary"
            onClick={onClose}
            disabled={isLoading}
            overrides={{
              BaseButton: {
                style: {
                  marginRight: '16px',
                }
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={isLoading}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #000)',
                }
              }
            }}
          >
            {initialData ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}; 